import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase/config'
import { createAuditLog, getChangedFields } from './audit'
import type { AgencySettings, UserRole } from '@/types'

// ============================================
// DEFAULT SETTINGS
// ============================================

export const defaultAgencySettings: Omit<AgencySettings, 'updatedAt' | 'updatedBy'> = {
  shifts: {
    dropRequiresReason: true,
    dropNoticeHoursMin: 24,
    dropRequiresApproval: false,
    pickupMode: 'auto_claim',
    pickupEligibilityRules: {
      requiresActiveStatus: true,
      requiresMatchingSkills: false,
      requiresCareTeam: false,
      requiresNotification: false,
    },
    coverageAlertHoursBefore: [48, 24, 12],
  },
  authorizations: {
    expirationAlertDays: [30, 14, 7],
    utilizationAlertThresholds: [75, 90, 100],
  },
  plansOfCare: {
    reviewAlertDaysBefore: 30,
    requireSignature: true,
    requireSupervisorApproval: true,
  },
  incidents: {
    types: [
      { id: 'fall', name: 'Fall', severity: 'high', requiresImmediateReview: true, notifyRoles: ['admin', 'supervisor'], isActive: true },
      { id: 'medication_error', name: 'Medication Error', severity: 'critical', requiresImmediateReview: true, notifyRoles: ['admin', 'supervisor'], isActive: true },
      { id: 'injury', name: 'Injury', severity: 'high', requiresImmediateReview: true, notifyRoles: ['admin', 'supervisor'], isActive: true },
      { id: 'behavioral', name: 'Behavioral Issue', severity: 'medium', requiresImmediateReview: false, notifyRoles: ['supervisor'], isActive: true },
      { id: 'property_damage', name: 'Property Damage', severity: 'low', requiresImmediateReview: false, notifyRoles: ['supervisor'], isActive: true },
      { id: 'other', name: 'Other', severity: 'low', requiresImmediateReview: false, notifyRoles: ['supervisor'], isActive: true },
    ],
    requiresPhotoForTypes: ['fall', 'injury', 'property_damage'],
    autoNotifyRoles: ['admin', 'supervisor'],
  },
  visitNotes: {
    requiredFields: ['summary', 'clientCondition'],
    taskChecklistEnabled: true,
    requireSignature: true,
    noteLockHours: 24,
  },
  notifications: {
    emailEnabled: true,
    smsEnabled: false,
    visitReminderHoursBefore: 2,
    dailyDigestEnabled: true,
  },
  security: {
    sessionTimeoutMinutes: 480,
    mfaRequiredRoles: [],
    passwordMinLength: 8,
    passwordRequireSpecialChar: true,
  },
  evv: {
    enabled: false,
    stateCode: 'VA',
    submissionMethod: 'embedded_claim',
    geofenceRadiusFeet: 1000,
    autoSubmit: false,
    clockInToleranceMinutes: 15,
    clockOutToleranceMinutes: 15,
    requireLocationForClockIn: true,
    requireLocationForClockOut: true,
    durationToleranceMinutes: 15,
  },
}

// ============================================
// GET SETTINGS
// ============================================

export async function getAgencySettings(agencyId: string): Promise<AgencySettings | null> {
  const docRef = doc(db, 'agencies', agencyId, 'settings', 'config')
  const snapshot = await getDoc(docRef)

  if (!snapshot.exists()) {
    return null
  }

  const data = snapshot.data() as Partial<AgencySettings>

  return {
    ...defaultAgencySettings,
    ...data,
    shifts: {
      ...defaultAgencySettings.shifts,
      ...(data.shifts || {}),
      pickupEligibilityRules: {
        ...defaultAgencySettings.shifts.pickupEligibilityRules,
        ...(data.shifts?.pickupEligibilityRules || {}),
      },
    },
    notifications: {
      ...defaultAgencySettings.notifications,
      ...(data.notifications || {}),
    },
  } as AgencySettings
}

// ============================================
// INITIALIZE SETTINGS
// ============================================

export async function initializeAgencySettings(
  agencyId: string,
  userId: string
): Promise<void> {
  const docRef = doc(db, 'agencies', agencyId, 'settings', 'config')

  await setDoc(docRef, {
    ...defaultAgencySettings,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })
}

// ============================================
// UPDATE SETTINGS
// ============================================

interface UpdateSettingsParams {
  agencyId: string
  settings: Partial<AgencySettings>
  userId: string
  userEmail: string
  userRole: UserRole
}

export async function updateAgencySettings(params: UpdateSettingsParams): Promise<void> {
  const { agencyId, settings, userId, userEmail, userRole } = params

  const docRef = doc(db, 'agencies', agencyId, 'settings', 'config')

  // Get current settings for audit
  const currentDoc = await getDoc(docRef)
  const currentSettings = currentDoc.exists() ? currentDoc.data() : {}

  const updateData = {
    ...settings,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  }

  if (currentDoc.exists()) {
    await updateDoc(docRef, updateData)
  } else {
    await setDoc(docRef, {
      ...defaultAgencySettings,
      ...updateData,
    })
  }

  // Determine which sections were changed
  const changedFields = getChangedFields(currentSettings, settings)

  await createAuditLog({
    agencyId,
    userId,
    userEmail,
    userRole,
    action: 'update',
    resource: 'settings',
    resourceId: 'config',
    description: `Updated agency settings: ${changedFields.join(', ')}`,
    previousValue: currentSettings as Record<string, unknown>,
    newValue: settings as Record<string, unknown>,
    changedFields,
  })
}

// ============================================
// UPDATE SPECIFIC SECTIONS
// ============================================

export async function updateShiftSettings(
  params: Omit<UpdateSettingsParams, 'settings'> & { shifts: AgencySettings['shifts'] }
): Promise<void> {
  return updateAgencySettings({
    ...params,
    settings: { shifts: params.shifts },
  })
}

export async function updateVisitNoteSettings(
  params: Omit<UpdateSettingsParams, 'settings'> & { visitNotes: AgencySettings['visitNotes'] }
): Promise<void> {
  return updateAgencySettings({
    ...params,
    settings: { visitNotes: params.visitNotes },
  })
}

export async function updateAuthorizationSettings(
  params: Omit<UpdateSettingsParams, 'settings'> & { authorizations: AgencySettings['authorizations'] }
): Promise<void> {
  return updateAgencySettings({
    ...params,
    settings: { authorizations: params.authorizations },
  })
}

export async function updateNotificationSettings(
  params: Omit<UpdateSettingsParams, 'settings'> & { notifications: AgencySettings['notifications'] }
): Promise<void> {
  return updateAgencySettings({
    ...params,
    settings: { notifications: params.notifications },
  })
}

export async function updateSecuritySettings(
  params: Omit<UpdateSettingsParams, 'settings'> & { security: AgencySettings['security'] }
): Promise<void> {
  return updateAgencySettings({
    ...params,
    settings: { security: params.security },
  })
}
